import { adminActionButtonSx } from 'src/theme/adminActionButton';
import { Button, CircularProgress, IconButton, Portal, Stack, Tooltip } from '@mui/material';
import { DownloadOutlined, EditOutlined } from '@mui/icons-material';
import React, { createContext, useContext } from 'react';

export const QuoteEventNameContext = createContext<(name: string) => void>(() => {});

export const QuoteNavigationActionsContext = createContext<HTMLDivElement | null>(null);

export const quoteEditorDisabledActionSx = {
  '&.Mui-disabled': {
    backgroundColor: '#f5f5f5',
    color: '#9e9e9e',
    border: '1px solid #e0e0e0'
  }
};

export const quoteEditorOutlineButtonSx = {
  width: { xs: '100%', sm: 150 },
  borderColor: '#b98f00',
  backgroundColor: '#ffffff',
  color: '#000000',
  '&:hover': { borderColor: '#000000', backgroundColor: '#fff8df' },
  ...quoteEditorDisabledActionSx
};

export const quoteEditorDocumentActionSx = {
  width: { xs: '100%', sm: 150 },
  ...adminActionButtonSx,
  '& .MuiButton-startIcon': { ml: 0, mr: 1 },
  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 }
};

interface QuoteEditorHeaderActionsProps {
  onEditHeader: () => void;
  onDownloadPdf?: () => void;
  editDisabled?: boolean;
  downloadDisabled?: boolean;
  downloading?: boolean;
  mode?: 'all' | 'edit' | 'download';
}

/** Stable header controls for both new and saved quote lifecycles. */
export default function QuoteEditorHeaderActions({
  onEditHeader,
  onDownloadPdf,
  editDisabled = false,
  downloadDisabled = false,
  downloading = false,
  mode = 'all'
}: QuoteEditorHeaderActionsProps) {
  const navigationContainer = useContext(QuoteNavigationActionsContext);
  return (
    <Portal container={navigationContainer} disablePortal={mode !== 'download' || !navigationContainer}>
    <Stack
      data-testid="shared-quote-editor-header-actions"
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent={mode === 'download' ? 'flex-end' : 'space-between'}
      spacing={1}
      sx={{
        '& .MuiButton-root': {
          width: 150,
          height: 34,
          minHeight: 34,
          py: 0,
          px: '12px',
          fontSize: 13,
          boxSizing: 'border-box',
          '& .MuiButton-startIcon': { ml: 0, mr: 1 },
          '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 }
        }
      }}
    >
      {mode !== 'download' && <Tooltip title="edited details" arrow>
        <span>
        <IconButton
        aria-label="edited details"
        onClick={onEditHeader}
        disabled={editDisabled}
        sx={{ width: 'auto', height: 34, px: 1, gap: 0.5, fontSize: 13, borderRadius: 1, color: '#E8BF21',
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: 'rgba(232,191,33,0.12)' } }}
      >
        <EditOutlined sx={{ fontSize: 22 }} />
        <span>Edit</span>
      </IconButton>
      </span>
      </Tooltip>}
      {mode !== 'edit' && <Button
        size="small"
        onClick={onDownloadPdf}
        startIcon={downloading ? undefined : <DownloadOutlined />}
        disabled={downloadDisabled || downloading || !onDownloadPdf}
        title={!onDownloadPdf ? 'Save the quote before downloading a PDF.' : undefined}
        sx={quoteEditorDocumentActionSx}
      >
        {downloading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          'Download PDF'
        )}
      </Button>}
    </Stack>
    </Portal>
  );
}
